<template>
  <div>
    <block v-model="loading"></block>

    <v-card flat class="pa-4 bar">
      <div class="d-flex justify-end align-center">
        <v-btn
          small
          class="btn-save ml-2"
          title="ABRIR BLOB"
          :disabled="!rows.length || loading"
          @click="openBlobPreview"
        >
          <v-icon>mdi-printer</v-icon>
        </v-btn>
        <v-btn
          small
          class="btn-save ml-2"
          title="DESCARGAR PDF"
          :disabled="!rows.length || loading"
          @click="exportPdf"
        >
          <v-icon>mdi-file-pdf-box</v-icon>
        </v-btn>
        <v-btn small class="btn-save ml-2" title="BUSCAR" @click="loadReport">
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
      </div>
    </v-card>

    <v-row class="mx-1 mt-5">
      <v-col cols="12" md="6">
        <v-select
          v-model="search.month"
          :items="monthList"
          item-text="text"
          item-value="value"
          label="MES"
          outlined
          attach
          dense
          :disabled="loading"
        ></v-select>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="search.year"
          label="AÑO"
          outlined
          type="number"
          attach
          dense
          clearable
          :disabled="loading"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-alert
      v-if="warnings.length"
      type="warning"
      outlined
      dense
      class="mx-1 mt-2"
    >
      <div class="font-weight-bold mb-2">ADVERTENCIAS</div>
      <ul class="mb-0 pl-4">
        <li v-for="warning in warnings" :key="warningKey(warning)">
          {{ warning.message }}
        </li>
      </ul>
    </v-alert>

    <v-alert
      v-if="emptyMessage"
      type="info"
      outlined
      dense
      class="mx-1 mt-2"
    >
      {{ emptyMessage }}
    </v-alert>

    <DxDataGrid
      class="consolidated-accounting-grid mt-3"
      :data-source="rows"
      :show-borders="true"
      :column-auto-width="true"
      :word-wrap-enabled="true"
      :paging="{ enabled: true, pageSize: 50 }"
      :pager="{
        visible: true,
        showInfo: true,
        showNavigationButtons: true,
        showPageSizeSelector: true,
        allowedPageSizes: [20, 50, 100],
      }"
      :scrolling="{ useNative: true }"
      no-data-text="NO SE ENCONTRARON DATOS"
    >
      <DxColumn
        width="22%"
        data-field="account_code"
        caption="CUENTA / PARTIDA"
        cell-template="account-template"
      />
      <DxColumn
        width="11%"
        data-field="fecha"
        caption="FECHA"
        cell-template="fecha-template"
      />
      <DxColumn
        width="39%"
        data-field="concepto"
        caption="CONCEPTO"
        cell-template="concept-template"
      />
      <DxColumn
        width="9%"
        data-field="saldo_anterior"
        caption="SALDO ANT."
        cell-template="money-template"
      />
      <DxColumn
        width="8%"
        data-field="debe"
        caption="DEBE"
        cell-template="money-template"
      />
      <DxColumn
        width="8%"
        data-field="haber"
        caption="HABER"
        cell-template="money-template"
      />
      <DxColumn
        width="8%"
        data-field="saldo_actual"
        caption="SALDO ACT."
        cell-template="money-template"
      />

      <template #account-template="{ data }">
        <span>{{ renderCuenta(data.data) }}</span>
      </template>

      <template #fecha-template="{ data }">
        <span>{{ renderFecha(data.data) }}</span>
      </template>

      <template #concept-template="{ data }">
        <span>{{ renderConcepto(data.data) }}</span>
      </template>

      <template #money-template="{ data }">
        <span
          class="money-cell"
          :class="{ 'money-cell--empty': !renderMoneyByField(data.data, data.column.dataField) }"
        >
          <span class="money-cell__symbol">{{ splitMoneyDisplay(data.data, data.column.dataField).symbol }}</span>
          <span class="money-cell__amount">{{ splitMoneyDisplay(data.data, data.column.dataField).amount }}</span>
        </span>
      </template>
    </DxDataGrid>

    <alerts
      v-if="alert.show"
      v-on:close_alert="closeAlert"
      v-on:accept_alert="acceptAlert"
      :properties="alert"
    ></alerts>
  </div>
</template>

<script>
import Block from "@/components/Block";
import Alerts from "@/components/Alerts.vue";
import { DxDataGrid, DxColumn } from "devextreme-vue/data-grid";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

export default {
  name: "ConsolidatedAccountingDetail",
  components: {
    Block,
    Alerts,
    DxDataGrid,
    DxColumn,
  },
  props: ["win"],
  data() {
    return {
      loading: false,
      rows: [],
      warnings: [],
      emptyMessage: "",
      search: {
        month: null,
        year: null,
      },
      monthList: [
        { text: "ENERO", value: 1 },
        { text: "FEBRERO", value: 2 },
        { text: "MARZO", value: 3 },
        { text: "ABRIL", value: 4 },
        { text: "MAYO", value: 5 },
        { text: "JUNIO", value: 6 },
        { text: "JULIO", value: 7 },
        { text: "AGOSTO", value: 8 },
        { text: "SEPTIEMBRE", value: 9 },
        { text: "OCTUBRE", value: 10 },
        { text: "NOVIEMBRE", value: 11 },
        { text: "DICIEMBRE", value: 12 },
      ],
      alert: {
        type: "success",
        show: false,
        header: "",
        body: "",
      },
      currencySymbol: JSON.parse(localStorage.getItem("branch"))?.Currency || "$",
      businessName: JSON.parse(localStorage.getItem("user"))?.business || "",
    };
  },
  computed: {
    hasWarnings() {
      return this.warnings.length > 0;
    },
    selectedMonthText() {
      return this.monthList.find((item) => item.value === Number(this.search.month))?.text || "";
    },
  },
  methods: {
    closeAlert() {
      this.alert.show = false;
    },
    acceptAlert() {
      this.alert.show = false;
    },
    showAlert(type, header, body) {
      this.alert.type = type || "danger";
      this.alert.header = header || "";
      this.alert.body = body || "";
      this.alert.show = true;
    },
    warningKey(warning) {
      return [
        warning?.type || "warning",
        warning?.id_account || "na",
        warning?.account_code || "na",
        warning?.message || "",
      ].join("|");
    },
    normalizeMoney(value) {
      if (value === null || value === undefined || value === "") {
        return 0;
      }

      if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
      }

      const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
      return Number.isNaN(parsed) ? 0 : parsed;
    },
    formatMoney(value) {
      const numeric = this.normalizeMoney(value);
      return `$ ${this.formatNumber(numeric)}`;
    },
    formatNumber(value) {
      const numeric = this.normalizeMoney(value);
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numeric);
    },
    toUpperText(value) {
      return String(value ?? "").toUpperCase();
    },
    renderPartida(row) {
      if (!row) {
        return "";
      }

      return this.toUpperText(row.comprobante || row.ID_Accounting_item || "");
    },
    renderFecha(row) {
      if (!row || row.row_type !== "MOVEMENT") {
        return "";
      }

      return this.toUpperText(row.fecha || "");
    },
    renderCuenta(row) {
      if (!row || row.row_type !== "ACCOUNT") {
        return this.toUpperText(row?.comprobante || row?.ID_Accounting_item || "");
      }

      return this.toUpperText(row.account_code || row.cuenta || row.comprobante || row.ID_Accounting_item || "");
    },
    renderConcepto(row) {
      if (!row) {
        return "";
      }

      return row.row_type === "ACCOUNT"
        ? this.toUpperText(row.account_description || row.concepto || "")
        : this.toUpperText(row.concepto || "");
    },
    renderMoneyByField(row, field) {
      if (!row || !field) {
        return "";
      }

      const normalizedField = String(field || "").toLowerCase();
      const value = this.normalizeMoney(
        row[field] ??
        row[normalizedField] ??
        row[normalizedField.charAt(0).toUpperCase() + normalizedField.slice(1)] ??
        row[normalizedField === "debe" ? "Debe" : normalizedField === "haber" ? "Haber" : field]
      );

      if (value === 0) {
        return "";
      }

      return `$ ${this.formatNumber(value)}`;
    },
    splitMoneyDisplay(row, field) {
      const displayValue = this.renderMoneyByField(row, field);
      if (!displayValue) {
        return { symbol: "", amount: "" };
      }

      const spaceIndex = displayValue.indexOf(" ");
      if (spaceIndex === -1) {
        return { symbol: "$", amount: displayValue };
      }

      return {
        symbol: displayValue.slice(0, spaceIndex),
        amount: displayValue.slice(spaceIndex + 1),
      };
    },
    validatePeriod() {
      const month = Number(this.search.month);
      const year = Number(this.search.year);

      if (!Number.isInteger(month) || month < 1 || month > 12) {
        this.showAlert("danger", "ERROR", "DEBE SELECCIONAR UN MES VÁLIDO");
        return null;
      }

      if (!Number.isInteger(year) || String(year).length !== 4) {
        this.showAlert("danger", "ERROR", "EL AÑO DEBE TENER 4 DÍGITOS");
        return null;
      }

      return { month, year };
    },
    async loadReport() {
      const period = this.validatePeriod();
      if (!period) {
        return;
      }

      this.loading = true;
      this.emptyMessage = "";

      try {
        const response = await this.$API.accountingreports.getAccountingDetailDataConsolidated({
          month: period.month,
          year: period.year,
        });

        const data = response || {};
        const rows = Array.isArray(data.rows)
          ? data.rows.map((row) => ({
              ...row,
              account_code: row.account_code || row.cuenta || "",
              account_description: row.account_description || row.concepto || "",
              comprobante: row.comprobante || row.ID_Accounting_item || "",
              debe: row.debe ?? row.Debe ?? 0,
              haber: row.haber ?? row.Haber ?? 0,
              saldo_anterior: row.saldo_anterior ?? row.SaldoAnterior ?? 0,
              saldo_actual: row.saldo_actual ?? row.SaldoActual ?? 0,
            }))
          : [];
        const warnings = Array.isArray(data.warnings) ? data.warnings : [];

        this.rows = rows;
        this.warnings = warnings;

        if (!rows.length) {
          this.emptyMessage = "NO SE ENCONTRARON MOVIMIENTOS NI CUENTAS PARA EL PERIODO SELECCIONADO.";
        }
      } catch (error) {
        this.rows = [];
        this.warnings = [];
        this.emptyMessage = "";
        this.showAlert("danger", "ERROR", this.toUpperText(error?.message || "NO FUE POSIBLE CONSULTAR EL LIBRO AUXILIAR CONSOLIDADO"));
      } finally {
        this.loading = false;
      }
    },
    async exportPdf() {
      const period = this.validatePeriod();
      if (!period || !this.rows.length) {
        return;
      }

      const doc = await this.buildPdfDocument(period);
      const fileName = `Libro_Auxiliar_Consolidado_${this.selectedMonthText}_${period.year}.pdf`;
      doc.save(fileName);
    },
    async openBlobPreview() {
      const period = this.validatePeriod();
      if (!period || !this.rows.length) {
        return;
      }

      const doc = await this.buildPdfDocument(period);
      const blob = doc.output("blob");
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 15000);
    },
    async buildPdfDocument(period) {
      const autoTable = (await import("jspdf-autotable")).default;
      const doc = new jsPDF("portrait", "mm", "letter");
      const pageWidth = doc.internal.pageSize.getWidth();

      const columns = [
        { header: "CUENTA / PARTIDA", dataKey: "cuenta" },
        { header: "FECHA", dataKey: "fecha" },
        { header: "CONCEPTO", dataKey: "concepto" },
        { header: "SALDO ANT.", dataKey: "saldo_anterior" },
        { header: "DEBE", dataKey: "debe" },
        { header: "HABER", dataKey: "haber" },
        { header: "SALDO ACT.", dataKey: "saldo_actual" },
      ];

      const body = this.rows.map((row) => ({
        cuenta: this.renderCuenta(row),
        fecha: this.renderFecha(row),
        concepto: this.renderConcepto(row),
        saldo_anterior: this.renderMoneyByField(row, "saldo_anterior"),
        debe: this.renderMoneyByField(row, "debe"),
        haber: this.renderMoneyByField(row, "haber"),
        saldo_actual: this.renderMoneyByField(row, "saldo_actual"),
        row_type: row.row_type,
      }));

      autoTable(doc, {
        columns,
        body,
        startY: 28,
        margin: { top: 22, bottom: 15, left: 8, right: 8 },
        theme: "plain",
        styles: {
          fontSize: 6.5,
          cellPadding: 1.1,
          valign: "middle",
          lineWidth: 0,
          textColor: 0,
        },
        headStyles: {
          fillColor: false,
          textColor: 0,
          halign: "center",
          fontStyle: "bold",
          lineWidth: 0.3,
          lineColor: 0,
        },
        columnStyles: {
          cuenta: { cellWidth: 28 },
          fecha: { cellWidth: 22, halign: "center" },
          concepto: { cellWidth: 67 },
          saldo_anterior: { cellWidth: 20, halign: "right" },
          debe: { cellWidth: 20, halign: "right" },
          haber: { cellWidth: 20, halign: "right" },
          saldo_actual: { cellWidth: 20, halign: "right" },
        },
        didParseCell: (hookData) => {
          const raw = hookData.row.raw || {};
          const isMoneyColumn = ["saldo_anterior", "debe", "haber", "saldo_actual"].includes(hookData.column.dataKey);

          if (hookData.section === "body" && isMoneyColumn) {
            hookData.cell.text = [""];
          }

          if (raw.row_type === "ACCOUNT") {
            hookData.cell.styles.fontStyle = "bold";
          }

          hookData.cell.styles.lineWidth = hookData.section === "head" ? 0.3 : 0;
        },
        didDrawCell: (hookData) => {
          const raw = hookData.row.raw || {};
          const isMoneyColumn = ["saldo_anterior", "debe", "haber", "saldo_actual"].includes(hookData.column.dataKey);

          if (!isMoneyColumn || hookData.section !== "body") {
            return;
          }

          const fieldValue = raw[hookData.column.dataKey] ?? raw[hookData.column.dataKey.charAt(0).toUpperCase() + hookData.column.dataKey.slice(1)];
          const numericValue = this.normalizeMoney(fieldValue);
          if (numericValue === 0) {
            return;
          }

          const symbol = "$";
          const formattedAmount = this.formatNumber(numericValue);
          const x = hookData.cell.x;
          const y = hookData.cell.y;
          const width = hookData.cell.width;
          const height = hookData.cell.height;
          const centerY = y + (height / 2) + 1.2;

          doc.setFontSize(6.5);
          doc.setFont("helvetica", "normal");
          doc.text(symbol, x + 1.2, centerY);
          doc.text(formattedAmount, x + width - 1.2, centerY, { align: "right" });
        },
        didDrawPage: () => {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.text(this.toUpperText(this.businessName || ""), pageWidth / 2, 8, { align: "center" });
          doc.setFontSize(9.5);
          doc.text(
            this.toUpperText(`LIBRO AUXILIAR AL ${this.endDateText(period)}`),
            pageWidth / 2,
            13,
            { align: "center" }
          );
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          doc.text(
            this.toUpperText("VALORES EXPRESADOS EN DOLARES DE LOS ESTADOS UNIDOS DE AMERICA"),
            pageWidth / 2,
            18,
            { align: "center" }
          );
          doc.text(
            `PÁGINA ${doc.internal.getCurrentPageInfo().pageNumber}/${doc.internal.getNumberOfPages()}`,
            pageWidth - 8,
            18,
            { align: "right" }
          );
        },
      });

      return doc;
    },
    endDateText(period) {
      const month = Number(period?.month);
      const year = Number(period?.year);
      if (!month || !year) {
        return "";
      }

      const lastDay = new Date(year, month, 0).getDate();
      return `${String(lastDay).padStart(2, "0")} de ${this.selectedMonthText} de ${year}`;
    },
  },
  mounted() {
    const today = new Date();
    this.search.month = today.getMonth() + 1;
    this.search.year = today.getFullYear();
  },
};
</script>

<style scoped>
.bar {
  max-width: 100%;
  background-color: rgb(60, 60, 60) !important;
  color: white !important;
  border-style: solid;
  border-color: black;
  border-radius: 15px !important;
  margin: 16px 12px !important;
}

.bar ::v-deep .btn-save.v-btn--disabled {
  background-color: #d0d0d0 !important;
  color: #6f6f6f !important;
  opacity: 0.65 !important;
  box-shadow: none !important;
}

.bar ::v-deep .btn-save.v-btn--disabled .v-icon {
  color: #6f6f6f !important;
}

.money-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.money-cell__symbol {
  min-width: 10px;
  text-align: left;
}

.money-cell__amount {
  flex: 1 1 auto;
  text-align: right;
}

.money-cell--empty {
  opacity: 0;
}
</style>
